/**
 * ConstructionHubTabs — the four tabs of Construction Hub (see ConstructionHubWorkspace.jsx and
 * docs/CONSTRUCTION_HUB.md). Read + navigate only: a row opens the item in its project's
 * Construction section, where all the real editing already lives. Same row-list look as
 * PermitsBoardTab.jsx so the two hubs read as siblings.
 */
import { useEffect, useMemo, useState } from 'react'
import { ctx } from '../outline/ctx.js'
import {
  getOpenConstructionItems, getAllConstructionContracts, getConstructionIntake, getContractorLibrary,
} from '../services/constructionHub.js'
import { markRfiSeen } from '../services/rfis.js'
import { markSubmittalSeen } from '../services/submittals.js'
import { markResponsesSeen } from '../services/proposalRequests.js'

/* ── helpers ─────────────────────────────────────────────────────────────── */

const fmtDate = s => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? `${+m[2]}/${+m[3]}/${m[1]}` : '' }
const fmtMoney = n => (n == null || n === '') ? '' : '$' + Math.round(Number(n)).toLocaleString('en-US')
function daysSince(s) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); if (!m) return null
  const then = new Date(+m[1], +m[2] - 1, +m[3]); const now = new Date(); now.setHours(0, 0, 0, 0)
  return Math.round((now - then) / 86400000)
}
const projectLabel = r => [r.projectNumber, r.projectName].filter(Boolean).join(' — ') || 'Unknown project'

const ZOOM_PREFIX = { rfi: 'rfi-detail:', submittal: 'submittal-detail:', asi: 'asi-detail:', pr: 'proposal-request-detail:', contract: 'contract-detail:' }

/** Opening an arrival counts as seeing it, exactly as opening its row in the project does. */
function markSeen(kind, id) {
  const fn = kind === 'rfi' ? markRfiSeen : kind === 'submittal' ? markSubmittalSeen : kind === 'pr' ? markResponsesSeen : null
  if (fn && id) Promise.resolve(fn(id)).catch(() => {})
}

/** Leave the hub and open the item inside its project's Construction section. */
function openInProject(projectId, kind, id) {
  if (!projectId) return
  ctx.closeAllWorkspaces()
  ctx.zoomToProject(projectId)
  // Same order ScheduleSection uses: project first, then the section, synchronously.
  const prefix = ZOOM_PREFIX[kind]
  if (prefix && id && ctx.zoomToSection) ctx.zoomToSection(prefix + id, { root: 'construction' })
  else if (ctx.zoomToRoot) ctx.zoomToRoot('construction')
}

function groupByProject(rows) {
  const m = new Map()
  rows.forEach(r => {
    const k = r.project_id || '—'
    if (!m.has(k)) m.set(k, { label: projectLabel(r), rows: [] })
    m.get(k).rows.push(r)
  })
  return Array.from(m.values()).sort((a, b) => a.label.localeCompare(b.label))
}

function useLoad(fn) {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [n, setN] = useState(0)
  useEffect(() => {
    let live = true
    setData(null); setError(null)
    fn().then(d => { if (live) setData(d) }).catch(e => { if (live) { setError(e.message || 'Could not load'); setData(null) } })
    return () => { live = false }
  }, [n]) // eslint-disable-line react-hooks/exhaustive-deps
  return { data, error, reload: () => setN(x => x + 1) }
}

function Status({ data, error, empty }) {
  if (error) return <div style={{ ...S.muted, color: '#c0392b' }}>{error}</div>
  if (data == null) return <div style={S.muted}>Loading…</div>
  if (empty) return <div style={S.muted}>{empty}</div>
  return null
}

function Pills({ options, value, onChange }) {
  return (
    <div style={S.pills}>
      {options.map(o => (
        <button key={o.value} style={{ ...S.pill, ...(value === o.value ? S.pillOn : {}) }} onClick={() => onChange(o.value)}>
          {o.label}{o.count != null ? <span style={S.pillCount}>{o.count}</span> : null}
        </button>
      ))}
    </div>
  )
}

/* ── Open Items ──────────────────────────────────────────────────────────── */

const KIND_LABEL = { rfi: 'RFIs', submittal: 'Submittals', asi: 'ASIs', pr: 'Proposal Requests' }

export function OpenItemsTab() {
  const { data, error } = useLoad(getOpenConstructionItems)
  const [kind, setKind] = useState('all')
  const [ours, setOurs] = useState(false)

  const rows = useMemo(() => (data || [])
    .filter(r => kind === 'all' || r.kind === kind)
    .filter(r => !ours || r.waitingOn === 'Alder James')
    .map(r => ({ ...r, age: daysSince(r.since) }))
    .sort((a, b) => (b.age ?? -1) - (a.age ?? -1)), [data, kind, ours])

  const counts = useMemo(() => {
    const c = { all: (data || []).length }
    ;(data || []).forEach(r => { c[r.kind] = (c[r.kind] || 0) + 1 })
    return c
  }, [data])

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <Pills value={kind} onChange={setKind} options={[
          { value: 'all', label: 'All', count: counts.all },
          ...['rfi', 'submittal', 'asi', 'pr'].map(k => ({ value: k, label: KIND_LABEL[k], count: counts[k] || 0 })),
        ]} />
        <button style={S.toggleBtn} onClick={() => setOurs(o => !o)}>
          {ours ? 'Show everything open' : 'Show only what is waiting on us'}
        </button>
      </div>
      <div style={S.note}>ASIs here are drafts that have not been issued yet. Oldest first.</div>
      <Status data={data} error={error} empty={data && rows.length === 0 ? 'Nothing open.' : null} />
      {groupByProject(rows).map(g => (
        <div key={g.label} style={S.group}>
          <div style={S.groupTitle}>{g.label}</div>
          {g.rows.map(r => {
            const overdue = r.due && daysSince(r.due) > 0
            return (
              <button key={r.kind + r.id} style={S.row} onClick={() => openInProject(r.project_id, r.kind, r.id)}>
                <span style={S.rowLabel}>{r.label}</span>
                <span style={S.rowMain}>{r.subject || <span style={S.dim}>No subject</span>}</span>
                <span style={S.rowSide}>{r.contractor}</span>
                <span style={{ ...S.rowSide, width: 120, color: r.waitingOn === 'Alder James' ? 'var(--aj-text)' : 'var(--aj-text-muted)' }}>
                  {r.waitingOn === 'Alder James' ? 'Waiting on us' : r.waitingOn}
                </span>
                <span style={{ ...S.rowNum, color: overdue ? '#c0392b' : 'var(--aj-text-muted)' }}>
                  {r.due ? (overdue ? 'overdue ' : 'due ') + fmtDate(r.due) : r.age != null ? r.age + 'd open' : ''}
                </span>
              </button>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/* ── Contracts & Portals ─────────────────────────────────────────────────── */

const CONTRACT_STATUS = { active: 'Active', closed: 'Closed out', on_hold: 'On hold' }

function portalText(p) {
  if (!p) return 'No portal'
  if (p.status !== 'active') return 'Portal off'
  const who = p.people === 1 ? '1 person' : p.people + ' people'
  return 'Portal on · ' + who + (p.lastOpened ? ' · last opened ' + fmtDate(p.lastOpened) : ' · never opened')
}

export function ContractsTab() {
  const { data, error } = useLoad(getAllConstructionContracts)
  const [which, setWhich] = useState('active')
  const rows = useMemo(() => (data || [])
    .filter(c => which === 'all' || c.status === 'active')
    .sort((a, b) => projectLabel(a).localeCompare(projectLabel(b))), [data, which])
  const activeCount = (data || []).filter(c => c.status === 'active').length

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <Pills value={which} onChange={setWhich} options={[
          { value: 'active', label: 'Active', count: activeCount },
          { value: 'all', label: 'All contracts', count: (data || []).length },
        ]} />
      </div>
      <Status data={data} error={error} empty={data && rows.length === 0 ? 'No contracts.' : null} />
      {rows.map(c => (
        <button key={c.id} style={S.row} onClick={() => openInProject(c.project_id, 'contract', c.id)}>
          <span style={{ ...S.rowMain, flex: '0 0 30%' }}>{projectLabel(c)}</span>
          <span style={S.rowMain}>
            {c.contractor_name || <span style={S.dim}>No contractor</span>}
            {c.contract_type ? <span style={S.dim}> · {c.contract_type}</span> : null}
          </span>
          <span style={S.rowNum}>{fmtMoney(c.contract_value)}</span>
          <span style={{ ...S.rowSide, width: 150 }}>
            {c.build_start_date || c.substantial_completion_date
              ? [fmtDate(c.build_start_date), fmtDate(c.substantial_completion_date)].map(x => x || '?').join(' → ')
              : <span style={S.dim}>No dates</span>}
          </span>
          <span style={{ ...S.rowSide, width: 230, color: c.portal && c.portal.status === 'active' ? 'var(--aj-text)' : 'var(--aj-text-muted)' }}>
            {portalText(c.portal)}
          </span>
          {which === 'all' && <span style={{ ...S.rowSide, width: 80 }}>{CONTRACT_STATUS[c.status] || 'No status'}</span>}
        </button>
      ))}
    </div>
  )
}

/* ── Contractors ─────────────────────────────────────────────────────────── */

export function ContractorsTab() {
  const { data, error } = useLoad(getContractorLibrary)
  const [q, setQ] = useState('')
  const [openId, setOpenId] = useState(null)
  const rows = useMemo(() => {
    const t = q.trim().toLowerCase()
    return (data || []).filter(o => !t || o.name.toLowerCase().includes(t) || o.types.join(' ').toLowerCase().includes(t))
  }, [data, q])

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <input style={S.search} placeholder="Search contractors" value={q} onChange={e => setQ(e.target.value)} />
        <button style={S.toggleBtn} onClick={() => window.dispatchEvent(new CustomEvent('open-contacts-panel'))}>
          Edit details in Contacts
        </button>
      </div>
      <div style={S.note}>Contractor companies from Contacts, with every job they have bid on or built for us.</div>
      <Status data={data} error={error} empty={data && rows.length === 0 ? 'No contractors found.' : null} />
      {rows.map(o => {
        const isOpen = openId === o.id
        const jobs = new Set([...o.contracts, ...o.bids, ...o.invites].map(x => x.project_id).filter(Boolean)).size
        return (
          <div key={o.id} style={S.orgBlock}>
            <button style={S.row} onClick={() => setOpenId(isOpen ? null : o.id)}>
              <span style={S.caret}>{isOpen ? '▾' : '▸'}</span>
              <span style={S.rowMain}>
                {o.name}{o.types.length ? <span style={S.dim}> · {o.types.join(', ')}</span> : null}
                {o.missing ? <span style={S.dim}> · not in Contacts</span> : null}
              </span>
              <span style={{ ...S.rowSide, width: 220 }}>{o.phone || o.email}</span>
              <span style={S.rowNum}>
                {o.contracts.length} built · {o.bids.length} bid{o.bids.length === 1 ? '' : 's'} · {jobs} job{jobs === 1 ? '' : 's'}
              </span>
            </button>
            {isOpen && (
              <div style={S.detail}>
                {(o.email || o.website) && (
                  <div style={S.detailLine}>
                    {o.email && <a style={S.link} href={'mailto:' + o.email}>{o.email}</a>}
                    {o.email && o.website ? '  ·  ' : ''}
                    {o.website && <a style={S.link} href={/^https?:/.test(o.website) ? o.website : 'https://' + o.website} target="_blank" rel="noreferrer">{o.website}</a>}
                  </div>
                )}
                {o.contracts.length === 0 && o.bids.length === 0 && o.invites.length === 0 && (
                  <div style={S.detailLine}><span style={S.dim}>No construction history in Cortex yet.</span></div>
                )}
                {o.contracts.map(c => (
                  <button key={'c' + c.id} style={S.subRow} onClick={() => openInProject(c.project_id, 'contract', c.id)}>
                    <span style={S.subTag}>Contract</span>
                    <span style={S.rowMain}>{projectLabel(c)}</span>
                    <span style={S.rowSide}>{CONTRACT_STATUS[c.status] || ''}</span>
                    <span style={S.rowNum}>{fmtMoney(c.contract_value)}</span>
                  </button>
                ))}
                {o.bids.map(b => (
                  <button key={'b' + b.id} style={S.subRow} onClick={() => openInProject(b.project_id, null, null)}>
                    <span style={S.subTag}>Bid</span>
                    <span style={S.rowMain}>{projectLabel(b)}{b.round ? <span style={S.dim}> · round {b.round}</span> : null}</span>
                    <span style={S.rowSide}>{b.won ? 'Won' : ''}</span>
                    <span style={S.rowNum}>{fmtMoney(b.price_normalized ?? b.price_raw)}</span>
                  </button>
                ))}
                {o.invites.filter(i => !o.bids.some(b => b.project_id === i.project_id && b.round === i.round)).map((i, k) => (
                  <button key={'i' + k} style={S.subRow} onClick={() => openInProject(i.project_id, null, null)}>
                    <span style={S.subTag}>Invited</span>
                    <span style={S.rowMain}>{projectLabel(i)}{i.round ? <span style={S.dim}> · round {i.round}</span> : null}</span>
                    <span style={S.rowSide}>No bid recorded</span>
                    <span style={S.rowNum}></span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

/* ── Intake ──────────────────────────────────────────────────────────────── */

const EMAIL_STATUS = {
  needs_project: 'Waiting for the forwarder to say which project',
  awaiting_reply: 'Possible duplicate, waiting for the forwarder',
  needs_attention: 'Could not be filed on its own',
  new: 'Stuck in processing for over an hour',
}

export function IntakeTab() {
  const { data, error } = useLoad(getConstructionIntake)
  const arrivals = useMemo(() => (data ? data.arrivals : []).slice().sort((a, b) => String(b.at).localeCompare(String(a.at))), [data])
  const emails = useMemo(() => (data ? data.emails : []).slice().sort((a, b) => String(b.at).localeCompare(String(a.at))), [data])

  return (
    <div style={S.wrap}>
      <div style={S.sectionTitle}>Sent by contractors, not opened yet <span style={S.pillCount}>{arrivals.length}</span></div>
      <div style={S.note}>These are already filed. Opening one in its project clears it from this list.</div>
      <Status data={data} error={error} empty={data && arrivals.length === 0 ? 'Nothing new.' : null} />
      {arrivals.map(a => (
        <button key={a.kind + a.id} style={S.row} onClick={() => { markSeen(a.kind, a.id); openInProject(a.project_id, a.kind === 'substitution' ? null : a.kind, a.id) }}>
          <span style={S.rowLabel}>{a.label}</span>
          <span style={S.rowMain}>{a.subject || <span style={S.dim}>No subject</span>}</span>
          <span style={{ ...S.rowSide, width: 200 }}>{projectLabel(a)}</span>
          <span style={S.rowSide}>{a.via === 'email' ? 'by email' : 'from the portal'}{a.amount != null ? ' · ' + fmtMoney(a.amount) : ''}</span>
          <span style={S.rowNum}>{fmtDate(a.at)}</span>
        </button>
      ))}

      <div style={{ ...S.sectionTitle, marginTop: 28 }}>Forwarded emails being held <span style={S.pillCount}>{emails.length}</span></div>
      <div style={S.note}>The person who forwarded each one was emailed about it. Replying to that email, or using its links, settles it.</div>
      {data && emails.length === 0 && <div style={S.muted}>Nothing held.</div>}
      {emails.map(e => (
        <div key={e.id} style={{ ...S.row, cursor: 'default' }}>
          <span style={S.rowMain}>{e.subject || <span style={S.dim}>No subject</span>}{e.from ? <span style={S.dim}> · from {e.from}</span> : null}</span>
          <span style={{ ...S.rowSide, width: 280 }} title={e.reason || ''}>{EMAIL_STATUS[e.status] || e.status}</span>
          <span style={{ ...S.rowSide, width: 160 }}>{e.forwardedBy ? 'forwarded by ' + e.forwardedBy.split('@')[0] : ''}</span>
          <span style={S.rowNum}>{fmtDate(e.at)}</span>
        </div>
      ))}
    </div>
  )
}

/* ── styles ──────────────────────────────────────────────────────────────── */

const S = {
  wrap: { padding: '18px 24px 48px', fontFamily: '"DM Sans",sans-serif', maxWidth: 1180 },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 },
  note: { fontSize: 12, color: 'var(--aj-text-muted)', marginBottom: 14 },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 },
  toggleBtn: { border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer', borderRadius: 4, padding: '6px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif' },
  search: { border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-surface)', color: 'var(--aj-text)', borderRadius: 4, padding: '6px 10px', fontSize: 13, fontFamily: '"DM Sans",sans-serif', width: 280 },
  pills: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  pill: { border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: 'pointer', borderRadius: 20, padding: '4px 12px', fontSize: 12, fontFamily: '"DM Sans",sans-serif', display: 'flex', alignItems: 'center', gap: 6 },
  pillOn: { background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', borderColor: 'var(--aj-text-mid)' },
  pillCount: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', fontWeight: 400 },
  muted: { color: 'var(--aj-text-muted)', fontSize: 13, padding: '8px 0' },
  dim: { color: 'var(--aj-text-muted)' },
  group: { marginBottom: 18 },
  groupTitle: { fontSize: 12, fontWeight: 600, color: 'var(--aj-text-mid)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' },
  row: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: 'none', borderBottom: '0.5px solid var(--aj-border)', background: 'transparent', cursor: 'pointer', padding: '8px 4px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  rowLabel: { flexShrink: 0, width: 170, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowMain: { flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowSide: { flexShrink: 0, width: 140, fontSize: 12, color: 'var(--aj-text-mid)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  rowNum: { fontFamily: '"DM Mono",monospace', fontSize: 11, flexShrink: 0, width: 160, textAlign: 'right', color: 'var(--aj-text-muted)' },
  caret: { flexShrink: 0, width: 12, fontSize: 11, color: 'var(--aj-text-muted)' },
  orgBlock: {},
  detail: { padding: '4px 0 10px 28px' },
  detailLine: { fontSize: 12, padding: '4px 0', color: 'var(--aj-text-mid)' },
  link: { color: 'var(--aj-accent-blue)', textDecoration: 'none' },
  subRow: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', border: 'none', borderBottom: '0.5px solid var(--aj-border)', background: 'transparent', cursor: 'pointer', padding: '6px 4px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)' },
  subTag: { flexShrink: 0, width: 64, fontSize: 11, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' },
}
